/**
 * MapForge — 모듈러 타일 키트.
 *
 * 게임 맵은 "타일 하나를 잘 만들고 격자에 찍는" 일이다. 이미지 한 장이 타일 하나가 된다:
 *   floor  이미지가 바닥 텍스처. 1×1 평면.
 *   wall   이미지가 벽면 텍스처. 1×1×0.15 상자, 앞뒤 면에 그림.
 *   prop   캐릭터와 같은 파이프라인(부풀리기)으로 만든 입체. 바닥에 세우고 셀 크기에 맞춘다.
 *
 * 문서(MapDoc)는 JSON 이다 — 자산은 이미지(dataURL)로, 배치는 셀 좌표·회전·층으로. 이미지에서
 * 자산을 다시 만들 수 있으니 문서 하나만 들고 다니면 된다. 내보내기는 장면 전체를 glb 하나로.
 */

import * as THREE from 'three';

export type TileKind = 'floor' | 'wall' | 'prop';

export interface TileAsset {
  id: string;
  name: string;
  kind: TileKind;
  /** 원본 이미지 dataURL (문서에 같이 저장) */
  image: string;
  thumb: string;
  /** 격자에 찍을 원형. 복제해서 쓴다. 바닥이 y=0, 셀 중심이 원점. */
  template: THREE.Object3D;
  dispose: () => void;
}

export interface Placement {
  x: number;
  z: number;
  assetId: string;
  /** 0..3 × 90° */
  rot: number;
  /** 층. 벽·프롭은 level 만큼 위로 (셀 높이 1) */
  level: number;
  scale: number;
}

export interface MapDoc {
  version: 1;
  name: string;
  width: number;
  depth: number;
  /** 주인공 스폰 셀 */
  spawn: { x: number; z: number; rot: number } | null;
  assets: { id: string; name: string; kind: TileKind; image: string }[];
  placements: Placement[];
}

export const KIND_LABEL: Record<TileKind, string> = { floor: '바닥', wall: '벽', prop: '프롭' };

export function emptyDoc(width = 12, depth = 12, name = 'map'): MapDoc {
  return { version: 1, name, width, depth, spawn: null, assets: [], placements: [] };
}

export const keyOf = (x: number, z: number, level: number) => `${x},${z},${level}`;

/** 같은 셀·같은 층에 이미 있으면 교체, 없으면 추가. floor 는 층 0 고정. */
export function placeTile(doc: MapDoc, p: Placement): MapDoc {
  const placements = doc.placements.filter((q) => !(q.x === p.x && q.z === p.z && q.level === p.level && sameLayer(doc, q.assetId, p.assetId)));
  placements.push(p);
  return { ...doc, placements };
}

/** 바닥은 바닥끼리, 벽·프롭은 서로 겹칠 수 있게 — 바닥 위에 프롭이 서야 하니까. */
function sameLayer(doc: MapDoc, a: string, b: string): boolean {
  const ka = doc.assets.find((x) => x.id === a)?.kind;
  const kb = doc.assets.find((x) => x.id === b)?.kind;
  return (ka === 'floor') === (kb === 'floor');
}

export function eraseTile(doc: MapDoc, x: number, z: number, level: number, onlyKind?: TileKind): MapDoc {
  return {
    ...doc,
    placements: doc.placements.filter((q) => {
      if (q.x !== x || q.z !== z || q.level !== level) return true;
      if (!onlyKind) return false;
      return doc.assets.find((a) => a.id === q.assetId)?.kind !== onlyKind;
    }),
  };
}

/** 바닥을 전부 같은 타일로 채운다 — 빈 격자에서 시작할 때 첫 동작. */
export function fillFloor(doc: MapDoc, assetId: string): MapDoc {
  const kept = doc.placements.filter((q) => doc.assets.find((a) => a.id === q.assetId)?.kind !== 'floor');
  for (let z = 0; z < doc.depth; z++) for (let x = 0; x < doc.width; x++) kept.push({ x, z, assetId, rot: 0, level: 0, scale: 1 });
  return { ...doc, placements: kept };
}

// ---------------------------------------------------------------------------
// 자산 원형
// ---------------------------------------------------------------------------

function textureFrom(image: HTMLImageElement | HTMLCanvasElement, srgb = true): THREE.Texture {
  const tex = new THREE.Texture(image);
  tex.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return tex;
}

export async function loadImage(src: string): Promise<HTMLImageElement> {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  await new Promise<void>((res, rej) => { img.onload = () => res(); img.onerror = () => rej(new Error('이미지를 못 읽었습니다')); img.src = src; });
  return img;
}

/** dataURL 로 (긴 변 max) — 문서에 들어가는 크기. */
export function toDataUrl(img: HTMLImageElement, max = 512, quality = 0.86): string {
  const s = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(img.naturalWidth * s));
  c.height = Math.max(1, Math.round(img.naturalHeight * s));
  c.getContext('2d')!.drawImage(img, 0, 0, c.width, c.height);
  return c.toDataURL('image/webp', quality);
}

/** 바닥·벽 — 이미지가 곧 텍스처. 정사각으로 크롭해 이음새를 줄인다. */
export function buildFlatTemplate(img: HTMLImageElement, kind: 'floor' | 'wall'): { object: THREE.Object3D; dispose: () => void } {
  const tex = textureFrom(img);
  const mat = new THREE.MeshStandardMaterial({ map: tex, roughness: 0.85, metalness: 0.02 });
  let mesh: THREE.Mesh;
  if (kind === 'floor') {
    mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.y = 0.001;
  } else {
    const g = new THREE.BoxGeometry(1, 1, 0.15);
    mesh = new THREE.Mesh(g, mat);
    mesh.position.set(0, 0.5, -0.5 + 0.075);
  }
  mesh.receiveShadow = true;
  mesh.castShadow = kind === 'wall';
  mesh.name = kind;
  const object = new THREE.Group();
  object.add(mesh);
  return { object, dispose: () => { tex.dispose(); mat.dispose(); mesh.geometry.dispose(); } };
}

/**
 * 프롭 — 파이프라인이 만든 메시(+머티리얼)를 받아 바닥에 세우고 셀에 맞춘다.
 * 가장 긴 가로 변이 셀의 fit 배가 되게 균일 스케일. 캐릭터 파이프라인은 +z 를 보므로 그대로.
 */
export function buildPropTemplate(geometry: THREE.BufferGeometry, material: THREE.Material, fit = 0.9): { object: THREE.Object3D; dispose: () => void } {
  const mesh = new THREE.Mesh(geometry, material);
  geometry.computeBoundingBox();
  const bb = geometry.boundingBox!;
  const size = new THREE.Vector3(); bb.getSize(size);
  const s = fit / Math.max(size.x, size.z, 1e-6);
  mesh.scale.setScalar(s);
  mesh.position.set(-(bb.min.x + bb.max.x) / 2 * s, -bb.min.y * s, -(bb.min.z + bb.max.z) / 2 * s);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  mesh.name = 'prop';
  const object = new THREE.Group();
  object.add(mesh);
  return { object, dispose: () => { /* 지오메트리·머티리얼은 자산이 소유 */ } };
}

// ---------------------------------------------------------------------------
// 장면 조립
// ---------------------------------------------------------------------------

/** 문서 → 장면. 원점을 격자의 (0,0) 셀 모서리에 두고 셀 중심은 +0.5. */
export function buildMapScene(doc: MapDoc, assets: Map<string, TileAsset>): THREE.Group {
  const root = new THREE.Group();
  root.name = doc.name || 'map';
  for (const p of doc.placements) {
    const a = assets.get(p.assetId);
    if (!a) continue;
    const inst = a.template.clone(true);
    inst.name = `${a.name}_${p.x}_${p.z}_${p.level}`;
    inst.position.set(p.x + 0.5, p.level, p.z + 0.5);
    inst.rotation.y = -p.rot * Math.PI / 2;
    inst.scale.setScalar(p.scale || 1);
    root.add(inst);
  }
  return root;
}

export function serializeDoc(doc: MapDoc): string {
  return JSON.stringify(doc);
}

export function parseDoc(text: string): MapDoc {
  const d = JSON.parse(text) as MapDoc;
  if (d.version !== 1 || !Array.isArray(d.placements) || !Array.isArray(d.assets)) throw new Error('맵 문서가 아닙니다');
  return d;
}

/** 문서 통계 — 패널 표시용 */
export function docStats(doc: MapDoc): { floors: number; walls: number; props: number } {
  const kind = new Map(doc.assets.map((a) => [a.id, a.kind] as const));
  const n = { floors: 0, walls: 0, props: 0 };
  for (const p of doc.placements) {
    const k = kind.get(p.assetId);
    if (k === 'floor') n.floors++; else if (k === 'wall') n.walls++; else if (k === 'prop') n.props++;
  }
  return n;
}
